import { Helmet } from 'react-helmet-async';
import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Container,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { api } from 'src/api/api-client.config';
import Iconify from '../components/iconify';
import Scrollbar from '../components/scrollbar';

import { useNavigate } from 'react-router-dom';
import PageDialog from '../components/pickem-admin/PageDialog';
import { DeleteDialog } from '../components/pickem-admin/DeleteDialog';
import { PageType } from '@/@types/api.types.ts';

export default function PagesList() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<PageType[]>([]);
  const [openPageDialog, setOpenPageDialog] = useState(false);
  const [showDeletePageDialog, setShowDeletePageDialog] = useState(false);

  const [editPage, setEditPage] = useState(null);
  const [deletePageId, setDeletePageId] = useState<number>(0);

  useEffect(() => {
    api.get<void, { pages: PageType[] }>(`/admin/pages`).then((data) => {
      setRows(data.pages.filter((item) => item !== null));
    });
  }, []);

  const deletePageHandler = (id: number) => {
    api.delete(`/admin/pages/${id}`).then(() => {
      setDeletePageId(0);
      window.location.reload();
    });
  };

  const successPageHandler = (page: PageType) => {
    if (page?.page_id) {
      api.put(`/admin/pages/${page.page_id}`, { ...page }).then(() => {
        window.location.reload();
      });
    } else {
      api.post(`/admin/pages`, { ...page }).then(() => {
        window.location.reload();
      });
    }
    setOpenPageDialog(false);
  };

  const closePageDialog = () => {
    setEditPage(null);
    setOpenPageDialog(false);
  };

  const openAddPageDialog = () => {
    setEditPage(null);
    setOpenPageDialog(true);
  };

  const openDeletePageDialog = (index: number) => {
    const page = rows[index];
    setDeletePageId(page.page_id);
    setShowDeletePageDialog(true);
  };

  return (
    <>
      <Helmet>
        <title>Pages</title>
      </Helmet>

      <Container>
        <Stack
          direction='row'
          alignItems='center'
          justifyContent='space-between'
          mb={5}
        >
          <Typography variant='h4' gutterBottom>
            Pages and Rules
          </Typography>
          <Button
            variant='contained'
            startIcon={<Iconify icon='eva:plus-fill' />}
            onClick={() => openAddPageDialog()}
          >
            New Page
          </Button>
        </Stack>

        <Card>
          {rows === null ? (
            <Box
              sx={{ display: 'flex', justifyContent: 'center' }}
              style={{ padding: 20 }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <Scrollbar>
              <TableContainer component={Paper}>
                <Table sx={{ minWidth: 650 }} aria-label='simple table'>
                  <TableHead>
                    <TableRow>
                      <TableCell>Route</TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {rows.map((row, index) => (
                      <TableRow
                        key={index}
                        sx={{
                          '&:last-child td, &:last-child th': { border: 0 },
                        }}
                      >
                        <TableCell component='th' scope='row'>
                          {row.page_key ?? ''}
                        </TableCell>

                        <TableCell component='th' scope='row'>
                          <Button
                            variant='outlined'
                            onClick={() =>
                              navigate(
                                `/dashboard/pages/edit?page_id=${row.page_id}`
                              )
                            }
                            startIcon={<Iconify icon='eva:edit-outline' />}
                            sx={{ mr: 2 }}
                          >
                            Edit
                          </Button>
                          <Button
                            variant='outlined'
                            onClick={() => openDeletePageDialog(index)}
                            startIcon={<Iconify icon='eva:trash-2-outline' />}
                          >
                            Delete
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Scrollbar>
          )}
        </Card>
      </Container>

      <PageDialog
        page={editPage}
        open={openPageDialog}
        onSuccessHandler={successPageHandler}
        onClose={closePageDialog}
      />

      <DeleteDialog
        openConfirm={showDeletePageDialog}
        setOpenConfirm={setShowDeletePageDialog}
        deleteHandler={deletePageHandler}
        id={deletePageId}
        type={'page'}
      />
    </>
  );
}
